import { PhoneIcon, ArrowRightIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import { useContent } from '../context/ContentContext';

export default function WhatWeHandle() {
  const { content, settings } = useContent();
  const wwh = content.whatWeHandle;

  return (
    <section className="py-16 sm:py-20 lg:py-20 xl:py-32 bg-white relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" />

      <div className="max-w-site mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="bg-brand-navy rounded-2xl sm:rounded-3xl overflow-hidden">
          <div className="grid lg:grid-cols-12">
            <div className="lg:col-span-5 p-6 sm:p-10 lg:p-14 flex flex-col justify-center">
              <div className="inline-flex items-center gap-2.5 mb-6 sm:mb-8">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping-slow absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
                </span>
                <span className="text-red-400 font-semibold text-sm uppercase tracking-wider">
                  {wwh.sectionLabel}
                </span>
              </div>

              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 sm:mb-5 leading-tight">
                {wwh.heading.split("We're On Our Way.")[0]}
                <br />
                <span className="text-brand-orange">We're On Our Way.</span>
              </h2>

              <p className="text-white/50 text-base sm:text-lg leading-relaxed mb-8 sm:mb-10">
                {wwh.description}
              </p>

              <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6 sm:mb-10 pb-6 sm:pb-10 border-b border-white/10">
                <div>
                  <p className="text-xl sm:text-3xl lg:text-4xl font-serif font-bold text-brand-orange">{wwh.stats.responseTime}</p>
                  <p className="text-white/40 text-[10px] sm:text-sm mt-1">Min Response</p>
                </div>
                <div>
                  <p className="text-xl sm:text-3xl lg:text-4xl font-serif font-bold text-white">{wwh.stats.availability}</p>
                  <p className="text-white/40 text-[10px] sm:text-sm mt-1">Availability</p>
                </div>
                <div>
                  <p className="text-xl sm:text-3xl lg:text-4xl font-serif font-bold text-white">{wwh.stats.overtimeFees}</p>
                  <p className="text-white/40 text-[10px] sm:text-sm mt-1">Overtime Fees</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <a
                  href={`tel:${settings.phone.replace(/[^0-9]/g, '')}`}
                  className="group inline-flex items-center justify-center gap-3 bg-brand-orange text-white px-7 py-4 rounded-xl font-semibold hover:bg-white hover:text-brand-navy transition-all duration-300 shadow-lg shadow-brand-orange/25"
                >
                  <PhoneIcon className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  <span>{settings.phone}</span>
                </a>
                <a
                  href={settings.bookingUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-white/70 font-semibold hover:text-brand-orange transition-colors duration-300"
                >
                  <span>{wwh.scheduleButtonLabel}</span>
                  <ArrowRightIcon className="w-4 h-4" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-7 bg-white/[0.04]">
              <div className="p-6 sm:p-10 lg:p-14">
                <div className="flex items-center gap-3 mb-6 sm:mb-8">
                  <div className="w-10 h-10 rounded-xl bg-brand-orange/10 flex items-center justify-center">
                    <ExclamationTriangleIcon className="w-5 h-5 text-brand-orange" />
                  </div>
                  <h3 className="text-white font-bold text-lg">{wwh.whatWeHandleHeading}</h3>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5 sm:gap-y-6">
                  {wwh.emergencyItems.map((item, index) => (
                    <div key={index} className="flex gap-3 sm:gap-4 group">
                      <div className="flex flex-col items-center pt-1.5">
                        <span className="w-2.5 h-2.5 rounded-full bg-brand-orange/40 group-hover:bg-brand-orange transition-colors duration-300 flex-shrink-0" />
                        {index < wwh.emergencyItems.length - 2 && (
                          <span className="w-px flex-1 bg-white/[0.06] mt-2 hidden sm:block" />
                        )}
                      </div>
                      <div className="pb-2">
                        <h4 className="font-semibold text-white text-[15px] mb-1 group-hover:text-brand-orange transition-colors duration-300">
                          {item.title}
                        </h4>
                        <p className="text-white/35 text-sm leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="mt-8 sm:mt-10 pt-6 sm:pt-8 border-t border-white/[0.06]">
                  <p className="text-white/30 text-sm leading-relaxed">
                    {wwh.disclaimer}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
